// Change #100326 - Settings > WhatsApp > Notifications: automated messages (event -> approved
// template) plus the recent send log. The event list comes from the API
// (MessagingNotificationLibrary::EVENTS) so a new event needs no frontend change.
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  createNotificationRule,
  deleteNotificationRule,
  errorText,
  listNotificationRules,
  listTemplates,
  notificationLog,
  ok,
  updateNotificationRule,
} from "../../../../Components/WhatsApp/whatsappApi";
import { approvalMeta, isSendable } from "./TemplatesTab";
import { DateCell, FieldHelp, ListState, Switch, Toolbar, channelName, isOn } from "./shared";

const UNITS = { minutes: 1, hours: 60, days: 1440 };

/** Largest whole unit for a stored minute count (1440 -> 1 day, 90 -> 90 minutes). */
const splitMinutes = (mins) => {
  const m = Number(mins) || 0;
  if (m > 0 && m % 1440 === 0) return { amount: m / 1440, unit: "days" };
  if (m > 0 && m % 60 === 0) return { amount: m / 60, unit: "hours" };
  return { amount: m, unit: m ? "minutes" : "hours" };
};

const describeOffset = (code, mins) => {
  const m = Number(mins) || 0;
  const after = /OVERDUE/.test(code);
  if (!m) return after ? "On the due date" : "At the time";
  const { amount, unit } = splitMinutes(m);
  const label = amount === 1 ? unit.replace(/s$/, "") : unit;
  return `${amount} ${label} ${after ? "after" : "before"}`;
};

const offsetHelp = (code, event) => {
  if (/OVERDUE/.test(code)) return "How long after the due date to send it (0 = on the due date).";
  const what = event?.entity === "FoodTableReservation" ? "reservation" : "appointment";
  return `How long before the ${what} to send it.`;
};

const LOG_STATUS = {
  SENT: "bg-success-subtle text-success",
  SKIPPED: "bg-warning-subtle text-warning",
  FAILED: "bg-danger-subtle text-danger",
};

const RuleModal = ({ open, row, events, channels, templates, onClose, onSaved }) => {
  const firstEvent = Object.keys(events)[0] || "";
  const blank = () => ({
    EventCode: firstEvent,
    MessagingChannel_Id: channels[0] ? String(channels[0].Id) : "",
    MessagingConversationTemplate_Id: "",
    amount: 1,
    unit: "days",
    FlagAttachPdf: 0,
    FlagActive: 1,
    Notes: "",
  });
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const isEdit = !!row?.Id;

  useEffect(() => {
    if (!open) return;
    if (row) {
      const { amount, unit } = splitMinutes(row.OffsetMinutes);
      setForm({
        EventCode: row.EventCode,
        MessagingChannel_Id: row.MessagingChannel_Id ? String(row.MessagingChannel_Id) : "",
        MessagingConversationTemplate_Id: row.MessagingConversationTemplate_Id ? String(row.MessagingConversationTemplate_Id) : "",
        amount,
        unit,
        FlagAttachPdf: isOn(row.FlagAttachPdf) ? 1 : 0,
        FlagActive: isOn(row.FlagActive) ? 1 : 0,
        Notes: row.Notes || "",
      });
    } else {
      const f = blank();
      const def = splitMinutes(events[f.EventCode]?.default_offset ?? 1440);
      setForm({ ...f, ...def });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  const event = events[form.EventCode];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const changeEvent = (code) => {
    const ev = events[code];
    setForm((f) => ({
      ...f,
      EventCode: code,
      ...(ev?.offset && !isEdit ? splitMinutes(ev.default_offset ?? 0) : {}),
      FlagAttachPdf: ev?.pdf ? f.FlagAttachPdf : 0,
    }));
  };

  const usableTemplates = useMemo(
    () =>
      templates.filter(
        (t) =>
          (isSendable(t) || String(t.Id) === form.MessagingConversationTemplate_Id) &&
          (t.FlagActive === null || t.FlagActive === undefined || isOn(t.FlagActive) || String(t.Id) === form.MessagingConversationTemplate_Id) &&
          (!t.MessagingChannel_Id || String(t.MessagingChannel_Id) === form.MessagingChannel_Id)
      ),
    [templates, form.MessagingChannel_Id, form.MessagingConversationTemplate_Id]
  );

  // Drop a chosen template that does not belong to a newly picked channel.
  useEffect(() => {
    if (templates.length && form.MessagingConversationTemplate_Id && !usableTemplates.some((t) => String(t.Id) === form.MessagingConversationTemplate_Id)) {
      setForm((f) => ({ ...f, MessagingConversationTemplate_Id: "" }));
    }
  }, [templates.length, usableTemplates, form.MessagingConversationTemplate_Id]);

  const canSave = form.EventCode && form.MessagingChannel_Id && form.MessagingConversationTemplate_Id && !saving;

  const save = async () => {
    if (!canSave) return;
    const payload = {
      EventCode: form.EventCode,
      MessagingChannel_Id: form.MessagingChannel_Id,
      MessagingConversationTemplate_Id: form.MessagingConversationTemplate_Id,
      OffsetMinutes: event?.offset ? Math.max(0, Math.round(Number(form.amount) || 0) * UNITS[form.unit]) : 0,
      FlagAttachPdf: event?.pdf ? form.FlagAttachPdf : 0,
      FlagActive: form.FlagActive,
      Notes: form.Notes,
    };
    if (isEdit) payload.Id = row.Id;
    setSaving(true);
    const res = await (isEdit ? updateNotificationRule(payload) : createNotificationRule(payload));
    setSaving(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not save the notification"));
      return;
    }
    toast.success(res.message || "Saved");
    onSaved();
  };

  const chosen = templates.find((t) => String(t.Id) === form.MessagingConversationTemplate_Id);

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit notification" : "New notification"}
      subtitle="Send an approved WhatsApp template when something happens"
      icon="ri-notification-3-line"
      headerAccent="emerald"
      size="md"
      loading={saving}
      onSubmit={save}
      submitDisabled={!canSave}
    >
      <Row className="g-3">
        <Col xs={12}>
          <Label htmlFor="wa-nr-event">When</Label>
          <Input id="wa-nr-event" type="select" value={form.EventCode} onChange={(e) => changeEvent(e.target.value)}>
            {Object.entries(events).map(([code, ev]) => (
              <option key={code} value={code}>
                {ev.label}
              </option>
            ))}
          </Input>
          {event?.manual && <FieldHelp>Sent when staff press the button on the job.</FieldHelp>}
        </Col>

        {event?.offset && (
          <Col xs={12}>
            <Label htmlFor="wa-nr-amount">Timing</Label>
            <div className="d-flex gap-2">
              <Input
                id="wa-nr-amount"
                type="number"
                min={0}
                className="wa-settings__offset"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
              />
              <Input type="select" aria-label="Unit" className="wa-settings__offset-unit" value={form.unit} onChange={(e) => set("unit", e.target.value)}>
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
              </Input>
            </div>
            <FieldHelp>{offsetHelp(form.EventCode, event)}</FieldHelp>
          </Col>
        )}

        <Col xs={12}>
          <Label htmlFor="wa-nr-channel">Send from</Label>
          <Input id="wa-nr-channel" type="select" value={form.MessagingChannel_Id} onChange={(e) => set("MessagingChannel_Id", e.target.value)}>
            {!channels.length && <option value="">Add a channel first</option>}
            {channels.map((c) => (
              <option key={c.Id} value={String(c.Id)}>
                {channelName(c)}
              </option>
            ))}
          </Input>
        </Col>

        <Col xs={12}>
          <Label htmlFor="wa-nr-tpl">Template</Label>
          <Input
            id="wa-nr-tpl"
            type="select"
            value={form.MessagingConversationTemplate_Id}
            onChange={(e) => set("MessagingConversationTemplate_Id", e.target.value)}
          >
            <option value="">{usableTemplates.length ? "Choose a template" : "No approved templates on this channel"}</option>
            {usableTemplates.map((t) => (
              <option key={t.Id} value={String(t.Id)}>
                {t.Name}
              </option>
            ))}
          </Input>
          <FieldHelp>Only approved templates can be sent automatically.</FieldHelp>
          {chosen && (
            <div className="wa-settings__preview-chat mt-2">
              <div className="wa-msg wa-msg--out">{chosen.Message}</div>
            </div>
          )}
        </Col>

        {event?.pdf && (
          <Col xs={12}>
            <Switch
              id="wa-nr-pdf"
              checked={isOn(form.FlagAttachPdf)}
              onChange={(v) => set("FlagAttachPdf", v)}
              label="Attach the PDF"
            />
            <FieldHelp>
              The PDF follows the template only if the customer has messaged you in the last 24 hours - WhatsApp does
              not allow attachments outside that window.
            </FieldHelp>
          </Col>
        )}

        <Col xs={12}>
          <Label htmlFor="wa-nr-notes">Notes</Label>
          <Input id="wa-nr-notes" type="textarea" rows={2} value={form.Notes} onChange={(e) => set("Notes", e.target.value)} />
        </Col>
        <Col xs={12}>
          <Switch id="wa-nr-active" checked={isOn(form.FlagActive)} onChange={(v) => set("FlagActive", v)} label="Active" />
        </Col>
      </Row>
    </AppModal>
  );
};

RuleModal.propTypes = {
  open: PropTypes.bool,
  row: PropTypes.object,
  events: PropTypes.object.isRequired,
  channels: PropTypes.array.isRequired,
  templates: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

const NotificationsTab = ({ channels }) => {
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState({});
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const load = async () => {
    setLoading(true);
    const [res, tpl] = await Promise.all([listNotificationRules(), listTemplates()]);
    setLoading(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not load notifications"));
    } else {
      setRules(res.messagingnotificationrule_list || []);
      setEvents(res.event_list || {});
    }
    if (ok(tpl)) setTemplates(tpl.messagingconversationtemplate_list || []);
  };

  const loadLog = async () => {
    setLogLoading(true);
    const res = await notificationLog();
    setLogLoading(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not load the send log"));
      return;
    }
    setLog(res.messagingnotificationlog_list || []);
  };

  useEffect(() => {
    load();
    loadLog();
  }, []);

  const toggleActive = async (rule, value) => {
    setTogglingId(rule.Id);
    const res = await updateNotificationRule({ Id: rule.Id, FlagActive: value });
    setTogglingId(null);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not update the notification"));
      return;
    }
    setRules((list) => list.map((r) => (r.Id === rule.Id ? { ...r, FlagActive: value } : r)));
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    const res = await deleteNotificationRule(deleting.Id);
    setDeleteBusy(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not delete the notification"));
      return;
    }
    toast.success(res.message || "Deleted");
    setDeleting(null);
    load();
  };

  const eventLabel = (code) => events[code]?.label || code;
  const canAdd = channels.length > 0 && Object.keys(events).length > 0;

  const newButton = (
    <Button color="primary" size="sm" onClick={() => setEditing({})} disabled={!canAdd}>
      <i className="ri-add-line me-1" />
      New notification
    </Button>
  );

  return (
    <>
      <div className="wa-settings__callout mb-3">
        <i className="ri-information-line me-1 text-primary" />
        Rules only apply to records created after the rule is switched on, and each record is only messaged once per
        rule. Numbers that replied STOP are skipped.
      </div>

      <Toolbar note="Automatic WhatsApp messages sent from an approved template.">{newButton}</Toolbar>

      <ListState
        loading={loading && !rules.length}
        empty={!rules.length}
        icon="ri-notification-3-line"
        title="No notifications set up"
        text={channels.length ? "Send booking confirmations, reminders and invoices on WhatsApp automatically." : "Add a WhatsApp channel first."}
        action={newButton}
      >
        <div className="table-responsive">
          <Table className="table-sm align-middle mb-0">
            <thead className="text-muted">
              <tr>
                <th>When</th>
                <th>Timing</th>
                <th>Template</th>
                <th>Channel</th>
                <th>PDF</th>
                <th>Active</th>
                <th className="text-end">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => {
                const ev = events[r.EventCode];
                const tplMeta = approvalMeta(r.Template_ApprovalStatus);
                const tplOk = !r.Template_ApprovalStatus || String(r.Template_ApprovalStatus).toLowerCase() === "approved";
                return (
                  <tr key={r.Id}>
                    <td>
                      <div className="fw-medium">{eventLabel(r.EventCode)}</div>
                      {ev?.manual && <div className="small text-muted">Sent from the button on the job</div>}
                      {r.Notes && <div className="small text-muted">{r.Notes}</div>}
                    </td>
                    <td className="text-nowrap">{ev?.offset ? describeOffset(r.EventCode, r.OffsetMinutes) : <span className="text-muted">Straight away</span>}</td>
                    <td>
                      {r.Template_Name || <span className="text-danger">Template missing</span>}
                      {r.Template_Name && !tplOk && <span className={`badge ${tplMeta.cls} ms-2 text-capitalize`}>{tplMeta.label}</span>}
                    </td>
                    <td>{r.MessagingChannel_DisplayName || r.MessagingChannel_Number || <span className="text-danger">Channel missing</span>}</td>
                    <td>{ev?.pdf ? (isOn(r.FlagAttachPdf) ? "Yes" : "No") : <span className="text-muted">-</span>}</td>
                    <td>
                      <Switch
                        id={`wa-nr-active-${r.Id}`}
                        checked={isOn(r.FlagActive)}
                        disabled={togglingId === r.Id}
                        onChange={(v) => toggleActive(r, v)}
                      />
                    </td>
                    <td className="text-end text-nowrap">
                      <Button color="light" size="sm" className="me-1" title="Edit" onClick={() => setEditing(r)}>
                        <i className="ri-pencil-fill align-bottom" />
                      </Button>
                      <Button color="light" size="sm" title="Delete" onClick={() => setDeleting(r)}>
                        <i className="ri-delete-bin-line align-bottom text-danger" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </ListState>

      <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
        <h6 className="mb-0">Recent sends</h6>
        <Button color="light" size="sm" onClick={loadLog} disabled={logLoading}>
          {logLoading ? (
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
          ) : (
            <i className="ri-refresh-line me-1" />
          )}
          Refresh
        </Button>
      </div>
      <ListState
        loading={logLoading && !log.length}
        empty={!log.length}
        icon="ri-history-line"
        title="Nothing sent yet"
        text="Every automatic send, skip and failure is listed here."
      >
        <div className="table-responsive wa-settings__log">
          <Table className="table-sm align-middle mb-0">
            <thead className="text-muted">
              <tr>
                <th>When</th>
                <th>Event</th>
                <th>Record</th>
                <th>Number</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {log.map((l) => (
                <tr key={l.Id}>
                  <td className="text-nowrap">
                    <DateCell value={l.CreatedDateTime} />
                  </td>
                  <td>{eventLabel(l.EventCode)}</td>
                  <td className="text-nowrap">
                    {l.EntityType} #{l.Entity_Id}
                  </td>
                  <td className="text-nowrap">{l.ContactNumber || <span className="text-muted">-</span>}</td>
                  <td>
                    <span className={`badge ${LOG_STATUS[l.Status] || "bg-secondary-subtle text-secondary"}`}>{l.Status}</span>
                  </td>
                  <td className="small">{l.ErrorMessage || <span className="text-muted">-</span>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </ListState>

      <RuleModal
        open={!!editing}
        row={editing && editing.Id ? editing : null}
        events={events}
        channels={channels}
        templates={templates}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />

      <DeleteModal
        show={!!deleting}
        loading={deleteBusy}
        onCloseClick={() => setDeleting(null)}
        onDeleteClick={confirmDelete}
        customText={`Delete the "${deleting ? eventLabel(deleting.EventCode) : ""}" notification? Nothing more will be sent for it.`}
      />
    </>
  );
};

NotificationsTab.propTypes = { channels: PropTypes.array.isRequired };

export default NotificationsTab;
